import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from '@cloudscape-design/components';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { useControlAuditLogs } from 'src/api/hooks/useAuditLogs';
import EmptyState from '../common/EmptyState/EmptyState';
import { columnDefinitions } from './AuditTrailColumnDefinition';
import AuditTrailPropertyFilter from './AuditTrailPropertyFilter';
import { flattenAuditLogs } from './utils';
import { AuditLogItem } from 'src/components/AuditTrail/interface';
import { getLeaseTypeFromUrl } from 'src/api/hooks/getControlData';
import { GRC5253_CONTROL_ID } from 'src/constants/grc5253';

export interface AuditPaginatedLogsTableProps {
  controlId: string;
}

const AuditTrailPaginatedTable = ({ controlId }: AuditPaginatedLogsTableProps) => {
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const TABLE_PAGE_SIZE = 50;
  const PAGINATED_FETCH_SIZE = 100;

  // Build filters JSON from URL leaseType for GRC5253 control
  const filters = useMemo(() => {
    if (controlId === GRC5253_CONTROL_ID) {
      const leaseType = getLeaseTypeFromUrl();
      if (leaseType) {
        return JSON.stringify({ leaseType: [leaseType] });
      }
    }
    return undefined;
  }, [controlId]);

  const [flattenedAuditLogs, setFlattenedAuditLogs] = useState<AuditLogItem[]>([]);

  const {
    data: fetchedLogs,
    isPending,
    isFetching,
    refetch,
  } = useControlAuditLogs({
    controlId,
    lastEvaluatedKey,
    pageSize: PAGINATED_FETCH_SIZE,
    filters,
  });

  // Populate flattenedAuditLogs whenever new items are fetched while using Set for deduplication
  useEffect(() => {
    if (fetchedLogs?.auditLogs) {
      setFlattenedAuditLogs((existingLogs) => {
        const keySet = new Set(existingLogs.map((auditLog) => auditLog.key));
        const addedLogs = flattenAuditLogs(fetchedLogs.auditLogs ?? []).filter(
          (auditLog) => !keySet.has(auditLog.key)
        );
        return [...existingLogs, ...addedLogs];
      });
    }
  }, [fetchedLogs?.auditLogs, isFetching]);

  const hasMoreData = !!fetchedLogs?.lastEvaluatedKey;

  const [currentPage, setCurrentPage] = useState(1);

  const { items, collectionProps, propertyFilterProps, filteredItemsCount } = useCollection(
    flattenedAuditLogs,
    {
      propertyFiltering: {
        filteringProperties: [
          {
            key: 'controlCode',
            operators: ['='],
            propertyLabel: 'Control Code',
            groupValuesLabel: 'Control Code values',
          },
          {
            key: 'modifiedBy',
            operators: ['='],
            propertyLabel: 'Modified By',
            groupValuesLabel: 'Modified By values',
          },
          {
            key: 'field',
            operators: ['='],
            propertyLabel: 'Modified Field',
            groupValuesLabel: 'Modified Field values',
          },
        ],
      },
    }
  );

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * TABLE_PAGE_SIZE;
    const endIndex = startIndex + TABLE_PAGE_SIZE;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage]);

  const loadMoreData = React.useCallback(() => {
    if (hasMoreData && !isFetching) {
      setLastEvaluatedKey(fetchedLogs?.lastEvaluatedKey);
    }
  }, [hasMoreData, isFetching, fetchedLogs?.lastEvaluatedKey]);

  const handleRefresh = React.useCallback(() => {
    if (!isFetching) {
      setLastEvaluatedKey(undefined);
      setFlattenedAuditLogs([]);
      setCurrentPage(1);
      refetch();
    }
  }, [refetch]);

  const filteringOptions = React.useMemo(() => {
    const uniqueOptions = new Set<string>();
    const options: { propertyKey: string; value: string }[] = [];

    flattenedAuditLogs.forEach((auditLogItem) => {
      const controlCodeKey = `controlCode-${auditLogItem?.controlCode}`;
      const modifiedByKey = `modifiedBy-${auditLogItem?.modifiedBy}`;
      const fieldKey = `field-${auditLogItem?.field}`;

      if (!uniqueOptions.has(controlCodeKey)) {
        uniqueOptions.add(controlCodeKey);
        options.push({ propertyKey: 'controlCode', value: auditLogItem?.controlCode as string });
      }

      if (!uniqueOptions.has(modifiedByKey)) {
        uniqueOptions.add(modifiedByKey);
        options.push({ propertyKey: 'modifiedBy', value: auditLogItem?.modifiedBy as string });
      }

      if (!uniqueOptions.has(fieldKey)) {
        uniqueOptions.add(fieldKey);
        options.push({ propertyKey: 'field', value: auditLogItem?.field as string });
      }
    });

    return options;
  }, [flattenedAuditLogs]);

  const tableColumns = columnDefinitions('Control') as TableProps.ColumnDefinition<any>[];

  return (
    <Table
      {...collectionProps}
      stickyHeader
      stripedRows
      items={paginatedItems}
      loading={isPending && paginatedItems.length == 0}
      contentDensity="compact"
      sortingDisabled={false}
      columnDefinitions={tableColumns}
      variant="container"
      loadingText="Loading audit logs"
      resizableColumns={true}
      header={
        <Header
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {
                <Button
                  disabled={!hasMoreData}
                  variant="normal"
                  onClick={loadMoreData}
                  loading={isFetching}
                  ariaLabel="Load more audit logs"
                >
                  Load More
                </Button>
              }
              <Button
                iconName="refresh"
                variant="normal"
                onClick={handleRefresh}
                loading={isFetching}
                ariaLabel="Refresh audit logs"
              >
                Refresh
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Box variant="h1">Audit Trail</Box>
            <Box variant="h3" color="text-body-secondary">
              ({filteredItemsCount})
            </Box>
            <Badge color="blue">{controlId}</Badge>
          </SpaceBetween>
        </Header>
      }
      empty={<EmptyState title="No audit logs found" description="Try adjusting your filters" />}
      filter={
        <AuditTrailPropertyFilter
          propertyFilterProps={propertyFilterProps}
          filteringOptions={filteringOptions}
          filteredItemsCount={filteredItemsCount}
        />
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          pagesCount={Math.ceil((filteredItemsCount ?? TABLE_PAGE_SIZE) / TABLE_PAGE_SIZE)}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
        />
      }
    />
  );
};

export default AuditTrailPaginatedTable;
